import type { BaseUser } from "@models";
import { Box, Button, Typography } from "@mui/material";
import type { Suggestion } from "@tiptap-pro/extension-tracked-changes";
import { UserAvatar } from "src/components/UserAvatar";
import { getVisibleTiptapSuggestionThreadComments } from "./tiptapCommentUtils";
import {
  formatTiptapSuggestionTime,
  getTiptapSuggestionAuthorDisplay,
  getTiptapSuggestionPreview,
  getTiptapSuggestionTypeLabel,
  truncateTiptapSuggestionText,
} from "./tiptapSuggestionAdapter";
import {
  TiptapCollaborationCardFrame,
  TiptapThreadDiscussion,
} from "./TiptapThreadDiscussion";
import type {
  TiptapCommentsController,
  TiptapCommentThread,
  TiptapSuggestionsController,
} from "./types";

type TiptapSuggestionCardProps = {
  commentsController?: TiptapCommentsController | null;
  controller: TiptapSuggestionsController;
  highlighted: number;
  linkedThread?: TiptapCommentThread | null;
  orgUsers: BaseUser[];
  suggestion: Suggestion;
};

export const TiptapSuggestionCard = ({
  commentsController,
  controller,
  highlighted,
  linkedThread,
  orgUsers,
  suggestion,
}: TiptapSuggestionCardProps) => {
  const formattedTime = formatTiptapSuggestionTime(suggestion.createdAt);
  const authorDisplay = getTiptapSuggestionAuthorDisplay(suggestion, orgUsers);
  const suggestionMetadata = [
    getTiptapSuggestionTypeLabel(suggestion),
    authorDisplay.coAuthorName
      ? `co-authored by ${authorDisplay.coAuthorName}`
      : null,
    formattedTime,
  ]
    .filter(Boolean)
    .join(" - ");
  const linkedThreadVisibleComments = linkedThread
    ? getVisibleTiptapSuggestionThreadComments(linkedThread)
    : [];

  return (
    <TiptapCollaborationCardFrame
      component="article"
      dataAttributes={{
        "data-change-type": suggestion.type,
        "data-selected": highlighted ? "true" : undefined,
        "data-suggestion-id": suggestion.id,
      }}
      highlighted={highlighted}
      highlightAnimationPrefix="tiptapSuggestionHighlightFade"
    >
      <Box
        data-testid={`tiptap-suggestion-card-header-${suggestion.id}`}
        style={{ alignItems: "flex-start", display: "flex", gap: 8 }}
      >
        <UserAvatar
          userId={authorDisplay.avatarUserId}
          name={authorDisplay.avatarName}
          imageSrc={authorDisplay.avatarSrc}
          imageAlt={authorDisplay.avatarName}
          size={21}
          fontSize={9}
        />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Box
            style={{
              alignItems: "flex-start",
              display: "flex",
              gap: 4,
              justifyContent: "space-between",
            }}
          >
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body1" style={{ color: "rgb(17, 24, 39)" }}>
                {authorDisplay.authorName}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Typography
        variant="caption"
        style={{
          color: "rgb(107, 114, 128)",
          display: "block",
          marginTop: 4,
        }}
      >
        {suggestionMetadata}
      </Typography>

      <Typography
        variant="body1"
        style={{
          color: "rgb(55, 65, 81)",
          fontSize: "1rem",
          marginTop: 8,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {getTiptapSuggestionPreview(suggestion)}
      </Typography>

      {suggestion.type === "replace" && suggestion.replacedText ? (
        <Typography
          variant="body2"
          style={{
            color: "rgb(107, 114, 128)",
            marginTop: 8,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          Was: {truncateTiptapSuggestionText(suggestion.replacedText)}
        </Typography>
      ) : null}

      <Box className="mt-3 flex justify-end gap-1">
        <Button
          size="small"
          variant="text"
          color="error"
          disabled={controller.disabled}
          onClick={() => controller.rejectSuggestion(suggestion.id)}
        >
          Reject
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={controller.disabled}
          onClick={() => controller.acceptSuggestion(suggestion.id)}
        >
          Accept
        </Button>
      </Box>

      {commentsController && linkedThread ? (
        <TiptapThreadDiscussion
          controller={commentsController}
          thread={linkedThread}
          variant="suggestion"
          visibleComments={linkedThreadVisibleComments}
        />
      ) : null}
    </TiptapCollaborationCardFrame>
  );
};
